import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <Seo
        title="Terms of service"
        description="Terms of service for TerminalValue.io — community-sourced index of investing tools."
        path="/terms"
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8 text-[14px] leading-relaxed text-[#222]">
        <h1 className="text-xl font-bold border-b border-black pb-2">{">"}_ terms of service</h1>

        <p className="text-[12px] text-[#888] mt-3">Last updated: June 2026</p>

        <Section title="1. What this site is">
          TerminalValue.io (&quot;the Site&quot;) is a community-sourced index of third-party investment
          tools. The Site itself does not provide investment advice, financial recommendations,
          brokerage services, or execute trades. Listings are editorial/curatorial and do not
          constitute endorsements.
        </Section>

        <Section title="2. Not investment advice">
          Nothing on the Site is intended as investment, legal, tax, or accounting advice. You are
          solely responsible for your own research and decisions. Any reliance on information on
          the Site is strictly at your own risk.
        </Section>

        <Section title="3. Third-party tools & links">
          The Site links to external products and services operated by third parties. We do not
          control these parties, do not guarantee the accuracy of the information they publish,
          and are not responsible for their content, pricing, practices, uptime, security, or
          conduct. Pricing tiers on the Site are our best-effort summaries and may be outdated.
        </Section>

        <Section title="4. Submissions">
          When you submit a tool or send us a message, you (a) confirm you have the right to do so,
          (b) grant us a perpetual, royalty-free, worldwide license to display, edit, summarize,
          and reproduce your submission on the Site and in reasonable promotional contexts, and
          (c) agree we may reject, edit, categorize, or remove any submission at our sole
          discretion without notice.
        </Section>

        <Section title="5. Acceptable use">
          You agree not to: (i) submit false, deceptive, promotional, or spam content;
          (ii) attempt to manipulate upvotes, rankings, or discovery signals; (iii) impersonate
          others; (iv) scrape the Site at rates that interfere with normal operation; (v) use the
          Site to distribute malware or violate any law.
        </Section>

        <Section title="6. Corrections & removals">
          If you are the owner of a tool listed on the Site and want a correction or removal,{" "}
          <Link to="/contact" className="tv-link">contact us</Link>. We review corrections and
          removals on a best-effort basis, typically within a few days. We do not guarantee any
          specific response time.
        </Section>

        <Section title="7. No warranties">
          The Site is provided &quot;as is&quot; and &quot;as available.&quot; We make no warranties,
          express or implied, including but not limited to warranties of merchantability, fitness
          for a particular purpose, non-infringement, accuracy, completeness, or continuous
          availability.
        </Section>

        <Section title="8. Limitation of liability">
          To the maximum extent permitted by law, TerminalValue.io and its operators shall not be
          liable for any indirect, incidental, consequential, special, or exemplary damages
          arising from or related to your use of the Site, including any losses from decisions
          made in reliance on listings or information on the Site.
        </Section>

        <Section title="9. Changes">
          We may change the Site, its features, its taxonomy, or these terms at any time. Material
          changes to the terms will be reflected by updating the &quot;last updated&quot; date above.
          Continued use of the Site after changes constitutes acceptance.
        </Section>

        <Section title="10. Termination">
          We may suspend or terminate access to any visitor or submitter at any time, for any
          reason, without notice.
        </Section>

        <Section title="11. Listing accuracy & AI-assisted content">
          Listings on the Site — including tool descriptions, summaries, categories, feature
          notes, and pricing tiers — are compiled on a best-effort basis and are{" "}
          <span className="font-bold">not guaranteed to be accurate, complete, or current</span>.
          Pricing and features change frequently; always verify directly with the vendor before
          relying on, subscribing to, or purchasing anything. Unless a specific item is explicitly
          marked otherwise, you should assume listing content is produced by a{" "}
          <span className="font-bold">mix of manual editorial entry and AI-assisted generation</span>.
          While we apply checks intended to keep AI-assisted content grounded in source material,
          it may still contain errors or omissions. Spotted something wrong?{" "}
          <Link to="/contact" className="tv-link">Let us know</Link>.
        </Section>

        <section className="mt-5" id="affiliate-disclosure">
          <h2 className="text-[14px] font-bold">12. Affiliate disclosure & commissions</h2>
          <p className="text-[13px] mt-1 text-[#222]">
            Some tools listed on the Site are operated by, or affiliated with, the operators of
            TerminalValue.io. These listings are marked with an{" "}
            <span className="font-bold">[affiliated]</span> tag. Affiliation does not affect
            editorial placement, ranking, or upvote counts. At time of writing, the
            affiliated listings are: <span className="font-bold">Tickler</span> (trytickler.com).
          </p>
          <p className="text-[13px] mt-2 text-[#222]">
            Separately, the Site may participate in affiliate / referral programs operated by
            tools listed in the index. When you click through to a third-party site via a link
            on the Site, we may earn a referral fee or commission at no additional cost to you.
            This does not influence which tools are listed, how they are categorized, where they
            rank, or what is written about them — editorial decisions and affiliate
            relationships are kept independent. Outbound clicks route through{" "}
            <code className="text-[12px] bg-[#f4f4f4] px-1">/api/go/&lt;slug&gt;</code>{" "}
            so we can transparently swap to a referral URL when one exists.
          </p>
        </section>

        <Section title="13. Contact">
          Questions about these terms? <Link to="/contact" className="tv-link">Send us a message</Link>.
        </Section>

        <p className="pt-6 text-[11px] text-[#888]">
          TerminalValue.io is an independent index. It is not a broker/dealer, exchange, or data
          vendor. Listings affiliated with the site operators are disclosed in section 12 above.
        </p>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-5">
      <h2 className="text-[14px] font-bold">{title}</h2>
      <p className="text-[13px] mt-1 text-[#222]">{children}</p>
    </section>
  );
}
